import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import CookieBanner from '../../components/feature/CookieBanner';

export default function HomePage() {
  const services = [
    {
      icon: 'ri-brush-line',
      title: 'Limpieza de hollín en superficies',
      description: 'Eliminación profesional de residuos de hollín en paredes, techos y suelos con técnicas especializadas.'
    },
    {
      icon: 'ri-windy-line',
      title: 'Eliminación de malos olores',
      description: 'Tratamiento avanzado para eliminar completamente los olores persistentes causados por el humo.'
    },
    {
      icon: 'ri-delete-bin-line',
      title: 'Desescombro y vaciado',
      description: 'Retirada de escombros, muebles dañados y materiales afectados por el incendio.'
    },
    {
      icon: 'ri-contrast-drop-line',
      title: 'Tratamiento de ozono',
      description: 'Desinfección profunda mediante ozono para purificar el aire y eliminar contaminantes.'
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Pintura final',
      description: 'Acabado profesional con pintura de alta calidad para restaurar el aspecto original.'
    },
    {
      icon: 'ri-tools-line',
      title: 'Pequeñas reparaciones',
      description: 'Reparaciones menores de albañilería, carpintería y otros elementos afectados.'
    }
  ];

  const benefits = [
    { icon: 'ri-time-line', text: 'Servicio 24/7 disponible' },
    { icon: 'ri-shield-check-line', text: 'Más de 15 años de experiencia' },
    { icon: 'ri-team-line', text: 'Equipo profesional certificado' },
    { icon: 'ri-price-tag-3-line', text: 'Presupuesto sin compromiso' }
  ];

  return (
    <>
      {/* Schema.org JSON-LD for Homepage */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "RESTAURAX - Limpieza y Restauración Post Incendio Hospitalet Barcelona",
          "description": "Especialistas en limpieza y restauración post incendio en Hospitalet de Llobregat y Barcelona. Servicio 24/7, más de 15 años de experiencia.",
          "url": `${import.meta.env.VITE_SITE_URL || 'https://restaurax.com'}/`,
          "mainEntity": {
            "@type": "LocalBusiness",
            "name": "RESTAURAX",
            "description": "Especialistas en limpieza y restauración post incendio",
            "telephone": "+34690220944",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Hospitalet de Llobregat",
              "addressRegion": "Barcelona",
              "addressCountry": "ES"
            },
            "serviceArea": {
              "@type": "GeoCircle",
              "geoMidpoint": {
                "@type": "GeoCoordinates",
                "latitude": 41.3598,
                "longitude": 2.0506
              },
              "geoRadius": "50000"
            }
          }
        })}
      </script>

      <div className="min-h-screen flex flex-col">
        <Header />
        
        {/* Hero Section */}
        <section 
          className="relative min-h-screen flex items-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/93f2e33f7a973a937223431ab8b947fa.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-black/50"></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full z-10">
            <div className="max-w-2xl">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                ¿Necesitas limpieza tras un incendio en Hospitalet de Llobregat?
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
                Nuestro equipo está preparado para actuar de inmediato. Cada minuto cuenta en la restauración post incendio.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="tel:690220944"
                  className="bg-[#F2D70A] text-[#544E13] px-6 sm:px-8 py-4 rounded-lg font-bold text-base sm:text-lg hover:bg-white transition-all text-center cursor-pointer flex items-center justify-center"
                >
                  <i className="ri-phone-line mr-2"></i>
                  <span className="whitespace-nowrap">Llamar Ahora: 690 22 09 44</span>
                </a>
                <Link 
                  to="/contacto"
                  className="bg-white/10 backdrop-blur-sm text-white border-2 border-white px-6 sm:px-8 py-4 rounded-lg font-bold text-base sm:text-lg hover:bg-white hover:text-[#544E13] transition-all text-center cursor-pointer whitespace-nowrap"
                >
                  Solicitar presupuesto gratis
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Bar */}
        <section className="bg-[#F2D70A] py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center justify-center space-x-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className={`${benefit.icon} text-2xl text-[#544E13]`}></i>
                  </div>
                  <span className="font-semibold text-[#544E13] text-sm md:text-base">{benefit.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#544E13] mb-6">
                  Expertos en limpieza post incendio en Barcelona
                </h2>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  <strong>RESTAURAX</strong> es la empresa líder en <strong>limpieza y restauración post incendio en Hospitalet de Llobregat</strong> y toda el área metropolitana de Barcelona. Con más de 15 años de experiencia en el sector, nos hemos consolidado como referentes en la recuperación de espacios afectados por incendios, ofreciendo soluciones integrales y profesionales.
                </p>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Entendemos que sufrir un incendio es una experiencia traumática y estresante. Por eso, nuestro equipo de profesionales certificados trabaja con rapidez, eficiencia y sensibilidad para devolver tu hogar o negocio a su estado original. Utilizamos las técnicas más avanzadas en <strong>limpieza de hollín</strong>, <strong>eliminación de olores</strong> y <strong>tratamiento de ozono</strong> para garantizar resultados óptimos.
                </p>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Nuestro servicio de <strong>restauración post incendio</strong> abarca desde el desescombro inicial hasta la pintura final, pasando por pequeñas reparaciones estructurales. Trabajamos directamente con compañías de seguros para facilitar todo el proceso y minimizar las molestias para nuestros clientes en Hospitalet de Llobregat, Barcelona, Cornellà, Esplugues y zonas cercanas. Si tienes dudas sobre nuestros procesos, consulta nuestras <Link to="/faq" className="text-[#F2D70A] hover:text-[#544E13] font-semibold underline">preguntas frecuentes</Link>.
                </p>
                <Link 
                  to="/empresa"
                  className="inline-block bg-[#544E13] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#F2D70A] hover:text-[#544E13] transition-all whitespace-nowrap cursor-pointer"
                >
                  Conoce Nuestra Empresa
                </Link>
              </div>
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20restoration%20team%20in%20protective%20gear%20cleaning%20fire%20damaged%20interior%2C%20modern%20equipment%20visible%2C%20professional%20cleaning%20of%20soot%20from%20walls%20and%20ceiling%2C%20bright%20professional%20photography%20showing%20detailed%20restoration%20work%2C%20safety%20protocols%20being%20followed&width=600&height=700&seq=intro-restaurax-002&orientation=portrait"
                  alt="Equipo profesional de limpieza post incendio"
                  className="rounded-lg shadow-2xl w-full h-auto object-cover object-top"
                  title="Limpieza profesional post incendio Barcelona"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#544E13] mb-4">
                Nuestros servicios de restauración post incendio
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Ofrecemos un servicio integral de limpieza y restauración tras incendios en Hospitalet de Llobregat y Barcelona, adaptado a las necesidades específicas de cada situación.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
              {services.map((service, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow">
                  <div className="w-16 h-16 bg-[#F2D70A] rounded-lg flex items-center justify-center mb-6">
                    <i className={`${service.icon} text-3xl text-[#544E13]`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#544E13] mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <Link 
                    to="/servicios"
                    className="text-[#F2D70A] font-semibold hover:text-[#544E13] transition-colors inline-flex items-center cursor-pointer"
                  >
                    Más información
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link 
                to="/servicios"
                className="inline-block bg-[#F2D70A] text-[#544E13] px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#544E13] hover:text-white transition-all whitespace-nowrap cursor-pointer"
              >
                Ver Todos los Servicios
              </Link>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#544E13] mb-12 text-center">
              ¿Por qué elegir RESTAURAX para tu restauración post incendio?
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#F2D70A] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-medal-line text-2xl text-[#544E13]"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#544E13] mb-2">Experiencia contrastada</h3>
                  <p className="text-gray-600">Más de 15 años especializados en limpieza y restauración post incendio en Barcelona. <Link to="/empresa" className="text-[#F2D70A] hover:text-[#544E13] font-semibold">Conoce nuestra historia</Link>.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#F2D70A] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-24-hours-line text-2xl text-[#544E13]"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#544E13] mb-2">Disponibilidad 24/7</h3>
                  <p className="text-gray-600">Servicio de emergencia disponible todos los días del año en Hospitalet de Llobregat. <Link to="/contacto" className="text-[#F2D70A] hover:text-[#544E13] font-semibold">Contáctanos ahora</Link>.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#F2D70A] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-star-line text-2xl text-[#544E13]"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#544E13] mb-2">Profesionales certificados</h3>
                  <p className="text-gray-600">Equipo altamente cualificado con formación continua en técnicas de restauración. <Link to="/servicios" className="text-[#F2D70A] hover:text-[#544E13] font-semibold">Ver todos nuestros servicios</Link>.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#F2D70A] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-tools-fill text-2xl text-[#544E13]"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#544E13] mb-2">Tecnología avanzada</h3>
                  <p className="text-gray-600">Equipamiento de última generación para limpieza de hollín y tratamiento de ozono.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#F2D70A] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-shield-line text-2xl text-[#544E13]"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#544E13] mb-2">Presupuestos para seguros</h3>
                  <p className="text-gray-600">Te entregamos presupuesto detallado para las compañías de seguros con toda la documentación necesaria.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#F2D70A] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-line text-2xl text-[#544E13]"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#544E13] mb-2">Garantía de calidad</h3>
                  <p className="text-gray-600">Garantizamos la calidad de todos nuestros trabajos de restauración post incendio.</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-[#F2D70A] to-[#e5ca09] rounded-lg p-8 text-center">
              <p className="text-2xl font-bold text-[#544E13] mb-4">
                ¿Necesitas una intervención urgente tras un incendio en Hospitalet de Llobregat?
              </p>
              <p className="text-lg text-[#544E13] mb-6">
                Nuestro equipo está preparado para actuar de inmediato. Cada minuto cuenta en la restauración post incendio.
              </p>
              <a 
                href="tel:690220944"
                className="inline-block bg-[#544E13] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-[#544E13] transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                Llamar Ahora: 690 22 09 44
              </a>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#544E13] mb-4 text-center">
              Nuestro proceso de restauración post incendio
            </h2>
            <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
              Seguimos un protocolo riguroso y profesional para garantizar la completa recuperación de tu espacio en Hospitalet de Llobregat y Barcelona.
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="bg-white rounded-lg p-6 shadow-lg text-center">
                <div className="w-16 h-16 bg-[#F2D70A] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-[#544E13]">1</span>
                </div>
                <h3 className="text-lg font-bold text-[#544E13] mb-3">Evaluación inicial</h3>
                <p className="text-gray-600">
                  Inspección detallada de los daños causados por el incendio y elaboración de un plan de actuación personalizado.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-lg text-center">
                <div className="w-16 h-16 bg-[#F2D70A] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-[#544E13]">2</span>
                </div>
                <h3 className="text-lg font-bold text-[#544E13] mb-3">Desescombro y limpieza</h3>
                <p className="text-gray-600">
                  Retirada de escombros, limpieza profunda de hollín y eliminación de materiales dañados de forma segura.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-lg text-center">
                <div className="w-16 h-16 bg-[#F2D70A] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-[#544E13]">3</span>
                </div>
                <h3 className="text-lg font-bold text-[#544E13] mb-3">Tratamiento especializado</h3>
                <p className="text-gray-600">
                  Aplicación de tratamiento de ozono para eliminar olores y desinfección completa del espacio afectado.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-lg text-center">
                <div className="w-16 h-16 bg-[#F2D70A] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-[#544E13]">4</span>
                </div>
                <h3 className="text-lg font-bold text-[#544E13] mb-3">Restauración final</h3>
                <p className="text-gray-600">
                  Pintura, pequeñas reparaciones y acabados finales para devolver el espacio a su estado original.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Service Area */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#544E13] mb-6">
                  Zona de servicio en Barcelona
                </h2>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Aunque nuestra sede principal está en <strong>Hospitalet de Llobregat</strong>, ofrecemos nuestros servicios de <strong>limpieza y restauración post incendio</strong> en toda el área metropolitana de Barcelona. Nuestro equipo puede desplazarse rápidamente a cualquier punto de la zona para atender emergencias.
                </p>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  <strong>Zonas de actuación:</strong> Hospitalet de Llobregat, Barcelona ciudad, Cornellà de Llobregat, Esplugues de Llobregat, Sant Just Desvern, Sant Feliu de Llobregat, El Prat de Llobregat, Viladecans, Sant Boi de Llobregat, Badalona, Santa Coloma de Gramenet, Sant Adrià de Besòs, Montgat, Tiana, Alella, El Masnou, Premià de Mar, Vilassar de Mar, Mataró, Sabadell, Terrassa, y municipios cercanos.
                </p>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Nuestro compromiso es ofrecer un servicio rápido y eficaz en toda la provincia de Barcelona, especialmente en situaciones de emergencia donde cada minuto cuenta para minimizar los daños causados por el incendio y el humo.
                </p>
                <Link 
                  to="/contacto"
                  className="inline-block bg-[#F2D70A] text-[#544E13] px-8 py-3 rounded-lg font-semibold hover:bg-[#544E13] hover:text-white transition-all whitespace-nowrap cursor-pointer"
                >
                  Solicitar Servicio en tu Zona
                </Link>
              </div>
              <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d95777.97502058595!2d2.0506!3d41.3598!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a4a2f8b5679c45%3A0x6c8b1b1b1b1b1b1b!2sHospitalet%20de%20Llobregat%2C%20Barcelona!5e0!3m2!1ses!2ses!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mapa de Hospitalet de Llobregat, Barcelona"
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-16 bg-gradient-to-br from-[#544E13] to-[#3a3610] text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              ¿Has sufrido un incendio? Actuamos de inmediato
            </h2>
            <p className="text-xl mb-8 text-gray-200">
              No esperes más. Contacta con RESTAURAX y comienza la recuperación de tu hogar o negocio en Hospitalet de Llobregat. Presupuesto gratuito y sin compromiso.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contacto"
                className="bg-[#F2D70A] text-[#544E13] px-8 py-4 rounded-lg font-bold text-lg hover:bg-white transition-all whitespace-nowrap cursor-pointer"
              >
                Solicitar Presupuesto Gratis
              </Link>
              <a 
                href="tel:690220944"
                className="bg-white/10 backdrop-blur-sm text-white border-2 border-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-[#544E13] transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                690 22 09 44
              </a>
            </div>
          </div>
        </section>

        <Footer />
        <WhatsAppButton />
        <ScrollToTopButton />
      </div>
    </>
  );
}